import React, { useEffect, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { config } from "../../config/config";

function ContactMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/contact-messages/?limit=200&offset=0`,
      );
      if (!response.ok) {
        throw new Error("Failed to load messages");
      }
      const data = await response.json();
      setMessages(data.items || []);
    } catch (error) {
      console.error("Error loading contact messages:", error);
      alert("Failed to load contact messages");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRead = async (id, isRead) => {
    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/contact-messages/${id}/read`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_read: !isRead }),
        },
      );
      if (!response.ok) {
        throw new Error("Failed to update message");
      }
      setMessages((prev) =>
        prev.map((msg) => (msg.id === id ? { ...msg, is_read: !isRead } : msg)),
      );
    } catch (error) {
      console.error("Error updating message:", error);
      alert("Failed to update message");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      const response = await fetch(
        `${config.api.baseURL}/admin/contact-messages/${id}`,
        {
          method: "DELETE",
        },
      );
      if (!response.ok) {
        throw new Error("Failed to delete message");
      }
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    } catch (error) {
      console.error("Error deleting message:", error);
      alert("Failed to delete message");
    }
  };

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <AdminLayout>
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Contact Messages
            </h1>
            <p className="text-sm text-gray-600">
              {messages.length} message{messages.length !== 1 ? "s" : ""}
            </p>
          </div>
          <button
            onClick={loadMessages}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-500">Loading...</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            No messages found
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`border rounded-lg p-4 ${
                  msg.is_read ? "bg-white" : "bg-blue-50"
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">
                        {msg.name}
                      </h3>
                      {!msg.is_read && (
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">
                          New
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">
                      {msg.email}
                      {msg.phone ? ` • ${msg.phone}` : ""}
                      {msg.company ? ` • ${msg.company}` : ""}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {msg.created_at}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleExpanded(msg.id)}
                      className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
                    >
                      {expandedId === msg.id ? "Hide" : "View"}
                    </button>
                    <button
                      onClick={() => handleToggleRead(msg.id, msg.is_read)}
                      className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
                    >
                      {msg.is_read ? "Mark Unread" : "Mark Read"}
                    </button>
                    <button
                      onClick={() => handleDelete(msg.id)}
                      className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {expandedId === msg.id && (
                  <div className="mt-4 text-gray-700 whitespace-pre-wrap">
                    {msg.message}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default ContactMessages;
